import { useState, useRef, useEffect, useLayoutEffect, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

interface PopoverProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}

interface PopoverTriggerProps {
  children: ReactNode;
  asChild?: boolean;
  className?: string;
  onClick?: () => void;
}

interface PopoverContentProps {
  children: ReactNode;
  className?: string;
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  /** Render via portal so the popover is never clipped by overflow containers. */
  portal?: boolean;
}

export function Popover({ open: controlledOpen, onOpenChange, children }: PopoverProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  const containerRef = useRef<HTMLDivElement>(null);

  const popoverId = useRef(`popover-${Math.random().toString(36).slice(2, 8)}`).current;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      // Check if click is inside the trigger container
      if (containerRef.current?.contains(target)) return;
      // Check if click is inside a portaled popover content belonging to this instance
      const portalEl = document.querySelector(`[data-popover-id="${popoverId}"]`);
      if (portalEl?.contains(target)) return;
      setOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, setOpen, popoverId]);

  return (
    <div ref={containerRef} className="relative" data-state={isOpen ? 'open' : 'closed'} data-popover-owner={popoverId}>
      {typeof children === 'function'
        ? (children as (props: { isOpen: boolean; setOpen: (v: boolean) => void }) => ReactNode)({ isOpen, setOpen })
        : children}
    </div>
  );
}

export function PopoverTrigger({ children, className, onClick }: PopoverTriggerProps) {
  return (
    <div className={className} onClick={onClick}>
      {children}
    </div>
  );
}

export function PopoverContent({
  children,
  className,
  align = 'start',
  portal = false,
}: PopoverContentProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const mountRef = useRef<HTMLSpanElement>(null);
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});
  const [ownerId, setOwnerId] = useState<string | null>(null);

  // Read the popover owner id from the DOM on mount
  useEffect(() => {
    if (!portal || !mountRef.current) return;
    const owner = mountRef.current.closest('[data-popover-owner]');
    if (owner) setOwnerId(owner.getAttribute('data-popover-owner'));
  }, [portal]);

  const updatePosition = useCallback(() => {
    if (!portal) return;
    const owner = ownerId ? document.querySelector(`[data-popover-owner="${ownerId}"]`) : null;
    if (!owner) return;
    const rect = owner.getBoundingClientRect();
    const style: React.CSSProperties = {
      position: 'fixed',
      zIndex: 9999,
      top: rect.bottom + 4,
    };
    if (align === 'end') {
      style.right = window.innerWidth - rect.right;
    } else if (align === 'center') {
      style.left = rect.left + rect.width / 2;
      style.transform = 'translateX(-50%)';
    } else {
      style.left = rect.left;
    }
    // Flip upward if overflowing bottom
    if (rect.bottom + 120 > window.innerHeight) {
      style.top = undefined;
      style.bottom = window.innerHeight - rect.top + 4;
    }
    setPortalStyle(style);
  }, [portal, align, ownerId]);

  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition]);

  if (portal) {
    return (
      <>
        {/* Hidden mount point to find the owner via DOM traversal */}
        <span ref={mountRef} className="hidden" />
        {ownerId && createPortal(
          <div
            ref={contentRef}
            data-popover-id={ownerId}
            style={portalStyle}
            className={cn(
              'fixed z-[9999] min-w-[8rem] rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-lg animate-in fade-in-0 zoom-in-95',
              className
            )}
          >
            {children}
          </div>,
          document.body
        )}
      </>
    );
  }

  return (
    <div
      className={cn(
        'absolute top-full mt-1 z-50 min-w-[8rem] rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-lg animate-in fade-in-0 zoom-in-95',
        align === 'start' && 'left-0',
        align === 'center' && 'left-1/2 -translate-x-1/2',
        align === 'end' && 'right-0',
        className
      )}
    >
      {children}
    </div>
  );
}
